import React, { useCallback, useEffect, useRef, useState } from 'react';
import Masonry from 'react-masonry-css';
import { useDispatch, useSelector } from 'react-redux';

import ProductModal from '@features/trend-research/components/common/ProductModal';
import AddMoodBoard from '@features/trend-research/components/moodboard/addMoodboard';

import { closeLoader, openLoader } from '@store/actions/LoaderAction';
import {
	useExploreProductsSource,
	useTrendResearchPreference
} from '@store/reducers/trend-research/TrendResearchReducer';

import { findTrendResearchProductsBySource } from '@services/trendResearch';

import { capitalizeFirstLetter, isPageReachBottom } from '@utils/Util';

const ExploreDesign = () => {
	const productRef = useRef();
	const dispatch = useDispatch();
	const loadingRef = useRef(false);
	const sourceType = useExploreProductsSource();
	const preferences = useTrendResearchPreference();
	const [products, setProducts] = useState([]);
	const [showModal, setShowModal] = useState(false);
	const [isAddToMoodBoard, setIsAddToMoodBoard] = useState(false);
	const [selectedProductId, setSelectedProductId] = useState(null);
	const [isOpenMoodBoardModal, setIsOpenMoodBoardModal] = useState(false);
	const filterData = useSelector((store) => store.filterFieldStore);
	const [selectedMoodBoardProduct, setSelectedMoodBoardProduct] = useState(null);

	const onClose = () => {
		setShowModal(false);
		setSelectedProductId(null);
	};

	const getProducts = async (page = 0) => {
		try {
			loadingRef.current = true;
			dispatch(openLoader());
			// &requestSource=EXPLORE
			let params = `?size=20&page=${page}`;
			if (filterData?.dateRange?.[0]?.key) {
				let selectedDate = filterData?.dateRange?.[0]?.key;
				let splitDate = selectedDate.split(',');
				params += `&startDate=${splitDate[0]}`;
				params += `&endDate=${splitDate[1]}`;
			}
			if (preferences?.categoryResponseList?.length > 0) {
				params += `&categoryIdList=${preferences.categoryResponseList
					.map((item) => item.id)
					.join(',')}`;
			}
			if (preferences?.fabricTypeList?.length > 0 && sourceType === 'BRAND') {
				params += `&fabricTypeList=${preferences.fabricTypeList
					.map((item) => item.constant)
					.join(',')}`;
			}
			if (preferences?.productGroupResponseList?.length > 0) {
				params += `&marketIdList=${preferences.productGroupResponseList
					.map((item) => item.id)
					.join(',')}`;
			}
			const data = await findTrendResearchProductsBySource(sourceType, params);
			if (data.currentPage > 0) {
				data.data = [...productRef.current.data, ...data.data];
			}
			productRef.current = data;
			setProducts(data.data);
			dispatch(closeLoader());
		} catch {
			dispatch(closeLoader());
		} finally {
			loadingRef.current = false;
		}
	};
	const handleScroll = useCallback(async () => {
		const { data, totalElements, currentPage } = productRef.current;

		if (
			isPageReachBottom() &&
			!loadingRef.current &&
			totalElements > 0 &&
			totalElements !== data.length
		) {
			await getProducts(currentPage + 1);
		}
	}, [filterData, sourceType, preferences, loadingRef, productRef]);

	useEffect(() => {
		if (preferences && Object.keys(preferences).length > 0) {
			(async () => {
				await getProducts(0);
				document.addEventListener('scroll', handleScroll);
			})();
			return () => {
				document.removeEventListener('scroll', handleScroll);
			};
		}
	}, [handleScroll]);

	const renderBrandLogo = (item) => {
		return (
			<div className="brand-logo">
				{item?.brandLogoDocResponse?.docUrl && (
					<img src={item?.brandLogoDocResponse?.docUrl} alt="dd" />
				)}
			</div>
		);
	};

	const renderInfluencerInfo = (item) => {
		return (
			<div
				className="instram-button d-flex align-items-center justify-content-center cursor-pointer"
				onClick={(e) => {
					e.stopPropagation();
					window.open(item.postUrl, '_blank');
				}}
			>
				<img
					className="inst-img"
					src={`${process.env.PUBLIC_URL}/icons/arrow_forward_dark.svg`}
					width={16}
					height={16}
					alt=""
				/>
				<img
					className="avatar mx-2"
					src={item?.userProfilePicUrl || `${process.env.PUBLIC_URL}/images/profile_avatar.png`}
					alt=""
					width={18}
					height={18}
				/>
				<p className="radius weight-500 gray_dark_01">{item?.userName}</p>
			</div>
		);
	};

	const renderProducts = () => {
		return products?.map((item, idx) => {
			return (
				<div
					className="masonry-item cursor-pointer"
					key={`explore_design_${idx}`}
					onClick={() => {
						setSelectedProductId(item.id);
						setShowModal(true);
					}}
				>
					<img className="radius-4" src={item.imageUrl} alt="" />
					<div className="card-overly radius-4 d-flex flex-column justify-content-between">
						<div className="logo-and-fab d-flex justify-content-between">
							{sourceType === 'BRAND' && renderBrandLogo(item)}
							{sourceType !== 'BRAND' && renderInfluencerInfo(item)}
							<div className="custom-chekbox">
								<div className="form-group d-flex mb-0">
									{selectedMoodBoardProduct?.id !== item.id && (
										<img
											src={`${process.env.PUBLIC_URL}/icons/not-favorite.svg`}
											alt="not-favourite"
											className="add-to-favorite cursor-pointer"
											onClick={async (e) => {
												e.stopPropagation();
												await handleMoodboardClick(item);
											}}
										/>
									)}
									{selectedMoodBoardProduct?.id === item.id && (
										<img
											src={`${process.env.PUBLIC_URL}/icons/favorite.svg`}
											alt="not-favourite"
											className="add-to-favorite cursor-pointer"
										/>
									)}
								</div>
							</div>
						</div>
						<div className="bottom-content text-left cursor-pointer">
							<p className="design-title semibold-14 text-white">
								{capitalizeFirstLetter(item.name)}
							</p>
							<ul className="common-list-component mt-1">
								{item?.price && <li className="text-white">${item.price}</li>}
								<li className="text-white">{item.brandName}</li>
							</ul>
						</div>
					</div>
				</div>
			);
		});
	};

	const handleMoodboardClick = async (product) => {
		setIsAddToMoodBoard(true);
		setIsOpenMoodBoardModal(true);
		setSelectedMoodBoardProduct(product);
	};

	const breakpointColumnsObj = {
		default: 5,
		1100: 3,
		700: 2,
		500: 1
	};

	return (
		<>
			<Masonry
				breakpointCols={breakpointColumnsObj}
				className="my-masonry-grid"
				columnClassName="my-masonry-grid_column"
			>
				{renderProducts()}
			</Masonry>

			{isOpenMoodBoardModal && (
				<AddMoodBoard
					isAddToMoodBoard={isAddToMoodBoard}
					setIsAddToMoodBoard={setIsAddToMoodBoard}
					isOpenMoodBoardModal={isOpenMoodBoardModal}
					setIsOpenMoodBoardModal={setIsOpenMoodBoardModal}
					selectedProduct={selectedMoodBoardProduct}
				/>
			)}

			{showModal && selectedProductId && (
				<ProductModal
					onClose={onClose}
					id={selectedProductId}
					typeOfSource={capitalizeFirstLetter(sourceType)}
				/>
			)}
		</>
	);
};

export default ExploreDesign;
